import type { User } from '../types/auth';
import { loadSignup } from '../features/signup/signupStorage';
import { returnPathFrom } from './returnPath';

/**
 * A signed-in Firebase account with no RosterPilot user behind it. Only a confirmed 401 from
 * `/api/auth/me` counts; a profile-less user from a transient failure is left alone.
 *
 * That is either an unfinished self-serve signup, or someone whose RosterPilot user was removed
 * (staff taken off a program, a deleted account). Which one is told apart by `isUnlinkedAccount`.
 */
export const isProfileMissing = (user: User | null | undefined): boolean =>
  user != null && !user.profile && user.profileMissing === true;

/**
 * A profile-less account with no signup under way in this browser: no stored signup form for its
 * address. Most often removed staff, who must not be dropped silently into self-serve signup. The
 * routes send such a user to `/login`, which signs them out and says the email is not linked to an
 * active account. Only an account with a matching stored signup is sent to `/signup` to resume it.
 */
export const isUnlinkedAccount = (user: User | null | undefined): boolean =>
  isProfileMissing(user) && loadSignup(user?.email) === null;

/**
 * Where a signed-in, profile-less user belongs: `/signup` to resume the signup stored for their
 * address, otherwise `/login` (which signs them out with an explanation).
 */
export const profileMissingTarget = (user: User): '/signup' | '/login' =>
  isUnlinkedAccount(user) ? '/login' : '/signup';

/**
 * Where `/login` sends someone who is already signed in and not an unlinked account (that one stays
 * on `/login` to be signed out): an unfinished signup back to `/signup` (never to a return path,
 * which would only bounce them there anyway), anyone else to the path a guard stashed, or the app's
 * default page.
 */
export const signedInLoginTarget = (user: User, locationState: unknown): string =>
  isProfileMissing(user) ? '/signup' : (returnPathFrom(locationState) ?? '/players');
